import React from "react";
import { connect } from "react-redux";
import Card from "components/Card/Card.jsx";
import { Grid, Row, Col } from "react-bootstrap";
import {
    getUserStats,
    getSubscriptionStats,
    getConnectedUsersCount,
    getTotalRevenue,
    getProductStats,
    getSubstanceStats,
    getEquivalenceStats,
    getAdditionalStats,
    getActiveUsersStats,
    updateCurrentUserLastActive
} from "../../../redux/actions/stats";
import PropTypes from 'prop-types';

class AdminDashboard extends React.Component {
    state = {
        totalUsers: 0,
        totalSubscribers: 0,
        connectedUsers: 0,
        totalRevenue: 0,
        monthlyStats: {
            users: [],
            revenue: []
        },
        selectedYear: new Date().getFullYear(),
        selectedMonth: new Date().getMonth() + 1,
        viewType: 'month',
        filteredData: {
            users: [],
            revenue: []
        }
    };

    componentDidMount() {
        // Mettre à jour immédiatement lastActive de l'utilisateur connecté
        this.props.updateCurrentUserLastActive();

        this.loadStats();
        this.interval = setInterval(() => {
            this.props.getConnectedUsersCount();
        }, 30000);
    }

    componentWillUnmount() {
        if (this.interval) {
            clearInterval(this.interval);
        }
    }

    handleYearChange = (e) => {
        const selectedYear = parseInt(e.target.value);
        this.setState({ selectedYear }, () => {
            this.loadStatsForYear(selectedYear);
        });
    };

    handleMonthChange = (e) => {
        this.setState({ selectedMonth: parseInt(e.target.value) });
    };

    handleViewTypeChange = (e) => {
        this.setState({ viewType: e.target.value });
    };

    loadStatsForYear = async (year) => {
        try {
            await Promise.all([
                this.props.getUserStats(year),
                this.props.getSubscriptionStats(year)
            ]);
        } catch (error) {
            // Erreur silencieuse lors du chargement des statistiques
        }
    };

    loadStats = async () => {
        try {
            await Promise.all([
                this.props.getUserStats(),
                this.props.getSubscriptionStats(),
                this.props.getConnectedUsersCount(),
                // this.props.getTotalRevenue(), // Supprimé car getSubscriptionStats calcule déjà totalRevenue
                this.props.getProductStats(),
                this.props.getSubstanceStats(),
                this.props.getEquivalenceStats(),
                this.props.getAdditionalStats(),
                this.props.getActiveUsersStats()
            ]);
        } catch (error) {
            // Erreur silencieuse lors du chargement des statistiques
        }
    };

    renderFilters = () => {
        const { selectedYear, selectedMonth, viewType } = this.state;
        const currentYear = new Date().getFullYear();
        const years = Array.from({ length: 5 }, (_, i) => currentYear - i);
        const months = [
            'Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin',
            'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'
        ];

        return (
            <div style={{
                padding: '15px',
                backgroundColor: '#f8f9fa',
                borderRadius: '8px',
                marginBottom: '20px',
                display: 'flex',
                gap: '15px',
                alignItems: 'center',
                flexWrap: 'wrap'
            }}>
                <div>
                    <label style={{ marginRight: '5px', fontWeight: 'bold' }}>Type de vue:</label>
                    <select
                        value={viewType}
                        onChange={this.handleViewTypeChange}
                        style={{ padding: '5px 10px', borderRadius: '4px', border: '1px solid #ddd' }}
                    >
                        <option value="day">Par jour</option>
                        <option value="month">Par mois</option>
                        <option value="year">Par année</option>
                    </select>
                </div>

                <div>
                    <label style={{ marginRight: '5px', fontWeight: 'bold' }}>Année:</label>
                    <select
                        value={selectedYear}
                        onChange={this.handleYearChange}
                        style={{ padding: '5px 10px', borderRadius: '4px', border: '1px solid #ddd' }}
                    >
                        {years.map(year => (
                            <option key={year} value={year}>{year}</option>
                        ))}
                    </select>
                </div>

                {viewType === 'day' && (
                    <div>
                        <label style={{ marginRight: '5px', fontWeight: 'bold' }}>Mois:</label>
                        <select
                            value={selectedMonth}
                            onChange={this.handleMonthChange}
                            style={{ padding: '5px 10px', borderRadius: '4px', border: '1px solid #ddd' }}
                        >
                            {months.map((month, index) => (
                                <option key={index} value={index + 1}>{month}</option>
                            ))}
                        </select>
                    </div>
                )}
            </div>
        );
    };

    renderBarChart = (data, label, unit = '') => {
        if (!data || !Array.isArray(data)) {
            return (
                <div style={{ padding: '20px', textAlign: 'center' }}>
                    <div style={{ color: '#e74c3c', marginBottom: '10px' }}>
                        <i className="fa fa-exclamation-triangle"></i> Données non disponibles
                    </div>
                    <small>Chargement des données...</small>
                </div>
            );
        }

        const numericData = data.map(val => Number(val)) || [];
        const maxValue = Math.max(...numericData, 1);
        const months = ['Jan', 'Fév', 'Mar', 'Avr', 'Mai', 'Jun', 'Jul', 'Aoû', 'Sep', 'Oct', 'Nov', 'Déc'];
        const total = numericData.reduce((a, b) => a + b, 0);

        return (
            <div style={{ padding: '20px' }}>
                {this.renderFilters()}

                <div style={{
                    display: 'flex',
                    alignItems: 'end',
                    justifyContent: 'space-around',
                    height: '200px',
                    borderBottom: '2px solid #ecf0f1',
                    paddingBottom: '10px'
                }}>
                    {numericData.map((value, index) => (
                        <div key={index} style={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            flex: 1,
                            maxWidth: '60px'
                        }}>
                            <div style={{
                                width: '30px',
                                height: `${Math.max((value / maxValue) * 150, value > 0 ? 10 : 2)}px`,
                                backgroundColor: value > 0 ? 'var(--app-primary-color)' : '#ecf0f1',
                                borderRadius: '4px 4px 0 0',
                                marginBottom: '5px',
                                transition: 'all 0.3s ease',
                                position: 'relative',
                                cursor: 'pointer',
                                border: value > 0 ? '2px solid var(--app-primary-color)' : '1px solid #bdc3c7'
                            }}
                                 title={`${months[index]}: ${value}${unit}`}>
                                {value > 0 && (
                                    <div style={{
                                        position: 'absolute',
                                        top: '-25px',
                                        left: '50%',
                                        transform: 'translateX(-50%)',
                                        fontSize: '10px',
                                        fontWeight: 'bold',
                                        color: '#2c3e50',
                                        backgroundColor: 'white',
                                        padding: '2px 4px',
                                        borderRadius: '3px',
                                        boxShadow: '0 1px 3px rgba(0,0,0,0.2)'
                                    }}>
                                        {value.toLocaleString()}
                                    </div>
                                )}
                            </div>
                            <small style={{
                                fontSize: '10px',
                                color: value > 0 ? '#2c3e50' : '#7f8c8d',
                                fontWeight: value > 0 ? 'bold' : 'normal'
                            }}>
                                {months[index]}
                            </small>
                        </div>
                    ))}
                </div>
                <div style={{
                    textAlign: 'center',
                    marginTop: '15px',
                    fontSize: '12px',
                    color: '#7f8c8d'
                }}>
                    Total cette année: <strong style={{ color: '#2c3e50' }}>
                    {total.toLocaleString()}{unit}
                </strong>
                    {total === 0 && (
                        <div style={{ marginTop: '10px', color: '#f39c12' }}>
                            <small>
                                <i className="fa fa-info-circle"></i>
                                Aucune donnée pour {this.state.selectedYear}.
                                Essayez une autre année ou vérifiez que les données existent.
                            </small>
                        </div>
                    )}
                </div>
            </div>
        );
    };

    render() {
        const {
            totalUsers,
            totalSubscribers,
            connectedUsers,
            totalRevenue,
            monthlyStats,
            totalProducts,
            totalSubstances,
            totalEquivalences,
            totalInteractions,
            totalPosologies,
            totalLaboratories
        } = this.props;

        const safeMonthlyStats = monthlyStats || { users: [], revenue: [] };

        const cardStyle = {
            backgroundColor: 'white',
            borderRadius: '14px',
            padding: '22px',
            textAlign: 'center',
            boxShadow: '0 6px 20px rgba(31, 58, 69, 0.06)',
            transition: 'all 0.3s ease',
            height: '180px',
            cursor: 'pointer',
            position: 'relative',
            overflow: 'hidden'
        };

        return (
            <div className="content admin-dashboard-page" style={{ backgroundColor: '#f6f9fa', minHeight: '100vh', padding: '24px' }}>
                <Grid fluid>
                    <Row style={{ marginBottom: '30px' }}>
                        <Col lg={3} md={6} sm={12} style={{ marginBottom: '20px' }}>
                            <div style={{
                                ...cardStyle,
                                border: 'none'
                            }}>
                                <div style={{ fontSize: '48px', color: 'var(--app-primary-color)', marginBottom: '15px' }}>
                                    <i className="fa fa-users"></i>
                                </div>
                                <h3 style={{ color: '#2c3e50', margin: '10px 0', fontSize: '28px' }}>
                                    {totalUsers || 0}
                                </h3>
                                <p style={{ color: '#7f8c8d', margin: 0, fontSize: '16px' }}>Utilisateurs</p>
                            </div>
                        </Col>

                        <Col lg={3} md={6} sm={12} style={{ marginBottom: '20px' }}>
                            <div style={{
                                ...cardStyle,
                                border: 'none'
                            }}>
                                <div style={{ fontSize: '48px', color: '#2ecc71', marginBottom: '15px' }}>
                                    <i className="fa fa-star"></i>
                                </div>
                                <h3 style={{ color: '#2c3e50', margin: '10px 0', fontSize: '28px' }}>
                                    {totalSubscribers || 0}
                                </h3>
                                <p style={{ color: '#7f8c8d', margin: 0, fontSize: '16px' }}>Abonnés Premium</p>
                            </div>
                        </Col>

                        <Col lg={3} md={6} sm={12} style={{ marginBottom: '20px' }}>
                            <div style={{
                                ...cardStyle,
                                border: 'none'
                            }}>
                                <div style={{ fontSize: '48px', color: '#f39c12', marginBottom: '15px' }}>
                                    <i className="fa fa-signal"></i>
                                </div>
                                <h3 style={{ color: '#2c3e50', margin: '10px 0', fontSize: '28px' }}>
                                    {connectedUsers || 0}
                                </h3>
                                <p style={{ color: '#7f8c8d', margin: 0, fontSize: '16px' }}>Utilisateurs Connectés</p>
                            </div>
                        </Col>

                        <Col lg={3} md={6} sm={12} style={{ marginBottom: '20px' }}>
                            <div style={{
                                ...cardStyle,
                                border: 'none'
                            }}>
                                <div style={{ fontSize: '48px', color: '#9b59b6', marginBottom: '15px' }}>
                                    <i className="fa fa-money"></i>
                                </div>
                                <h3 style={{ color: '#2c3e50', margin: '10px 0', fontSize: '28px' }}>
                                    {totalRevenue ? `${totalRevenue.toLocaleString()} F` : '0 F'}
                                </h3>
                                <p style={{ color: '#7f8c8d', margin: 0, fontSize: '16px' }}>Revenus Totaux</p>
                            </div>
                        </Col>
                    </Row>

                    <Row>
                        <Col md={12}>
                            <Card
                                title="Statistiques des Données"
                                style={{ border: 'none', boxShadow: 'none' }}
                                content={
                                    <Row>
                                        <Col md={4} sm={6}>
                                            <div style={{
                                                backgroundColor: 'var(--app-primary-tint)',
                                                borderRadius: '8px',
                                                padding: '15px',
                                                marginBottom: '20px',
                                                textAlign: 'center'
                                            }}>
                                                <h4 style={{ color: '#073b46', marginBottom: '10px' }}>
                                                    <i className="fa fa-medkit"></i> Produits
                                                </h4>
                                                <h3 style={{ color: '#073b46' }}>{totalProducts || 0}</h3>
                                            </div>
                                        </Col>
                                        <Col md={4} sm={6}>
                                            <div style={{
                                                backgroundColor: 'var(--app-primary-tint)',
                                                borderRadius: '8px',
                                                padding: '15px',
                                                marginBottom: '20px',
                                                textAlign: 'center'
                                            }}>
                                                <h4 style={{ color: '#073b46', marginBottom: '10px' }}>
                                                    <i className="fa fa-flask"></i> Substances
                                                </h4>
                                                <h3 style={{ color: '#073b46' }}>{totalSubstances || 0}</h3>
                                            </div>
                                        </Col>
                                        <Col md={4} sm={6}>
                                            <div style={{
                                                backgroundColor: 'var(--app-primary-tint)',
                                                borderRadius: '8px',
                                                padding: '15px',
                                                marginBottom: '20px',
                                                textAlign: 'center'
                                            }}>
                                                <h4 style={{ color: '#073b46', marginBottom: '10px' }}>
                                                    <i className="fa fa-exchange"></i> Équivalences
                                                </h4>
                                                <h3 style={{ color: '#073b46' }}>{totalEquivalences || 0}</h3>
                                            </div>
                                        </Col>
                                        <Col md={4} sm={6}>
                                            <div style={{
                                                backgroundColor: 'var(--app-primary-tint)',
                                                borderRadius: '8px',
                                                padding: '15px',
                                                marginBottom: '20px',
                                                textAlign: 'center'
                                            }}>
                                                <h4 style={{ color: '#073b46', marginBottom: '10px' }}>
                                                    <i className="fa fa-random"></i> Interactions
                                                </h4>
                                                <h3 style={{ color: '#073b46' }}>{totalInteractions || 0}</h3>
                                            </div>
                                        </Col>
                                        <Col md={4} sm={6}>
                                            <div style={{
                                                backgroundColor: 'var(--app-primary-tint)',
                                                borderRadius: '8px',
                                                padding: '15px',
                                                marginBottom: '20px',
                                                textAlign: 'center'
                                            }}>
                                                <h4 style={{ color: '#073b46', marginBottom: '10px' }}>
                                                    <i className="fa fa-list-alt"></i> Posologies
                                                </h4>
                                                <h3 style={{ color: '#073b46' }}>{totalPosologies || 0}</h3>
                                            </div>
                                        </Col>
                                        <Col md={4} sm={6}>
                                            <div style={{
                                                backgroundColor: 'var(--app-primary-tint)',
                                                borderRadius: '8px',
                                                padding: '15px',
                                                marginBottom: '20px',
                                                textAlign: 'center'
                                            }}>
                                                <h4 style={{ color: '#073b46', marginBottom: '10px' }}>
                                                    <i className="fa fa-building"></i> Laboratoires
                                                </h4>
                                                <h3 style={{ color: '#073b46' }}>{totalLaboratories || 0}</h3>
                                            </div>
                                        </Col>
                                    </Row>
                                }
                            />
                        </Col>
                    </Row>

                    <Row>
                        <Col md={6}>
                            <Card
                                title="Évolution des Inscriptions"
                                content={this.renderBarChart(safeMonthlyStats.users, 'Nouveaux Utilisateurs')}
                            />
                        </Col>
                        <Col md={6}>
                            <Card
                                title="Évolution des Revenus"
                                content={this.renderBarChart(safeMonthlyStats.revenue, 'Revenus', ' F')}
                            />
                        </Col>
                    </Row>

                    <Row>
                        <Col md={12}>
                            <Card
                                title="Statistiques des Forfaits"
                                content={
                                    <div style={{ padding: '20px' }}>
                                        <Row>
                                            <Col md={4}>
                                                <div style={{
                                                    backgroundColor: '#282a36',
                                                    borderRadius: '8px',
                                                    padding: '15px',
                                                    marginBottom: '20px',
                                                    borderLeft: '4px solid #bd93f9',
                                                    textAlign: 'center',
                                                    boxShadow: '0 4px 10px rgba(0,0,0,0.2)'
                                                }}>
                                                    <h4 style={{ color: '#ff79c6', marginBottom: '10px' }}>
                                                        <i className="fa fa-calendar"></i> Forfait Annuel
                                                    </h4>
                                                    <h3 style={{ color: '#f8f8f2' }}>{this.props.subscriptionStats?.annual?.count || 0} utilisateurs</h3>
                                                    <div style={{ color: '#6272a4', marginTop: '10px' }}>
                                                        Total: <strong style={{ color: '#50fa7b' }}>{(this.props.subscriptionStats?.annual?.revenue || 0).toLocaleString()} F</strong>
                                                    </div>
                                                </div>
                                            </Col>
                                            <Col md={4}>
                                                <div style={{
                                                    backgroundColor: '#282a36',
                                                    borderRadius: '8px',
                                                    padding: '15px',
                                                    marginBottom: '20px',
                                                    borderLeft: '4px solid #ff79c6',
                                                    textAlign: 'center',
                                                    boxShadow: '0 4px 10px rgba(0,0,0,0.2)'
                                                }}>
                                                    <h4 style={{ color: '#ff79c6', marginBottom: '10px' }}>
                                                        <i className="fa fa-calendar"></i> Forfait Trimestriel
                                                    </h4>
                                                    <h3 style={{ color: '#f8f8f2' }}>{this.props.subscriptionStats?.quarterly?.count || 0} utilisateurs</h3>
                                                    <div style={{ color: '#6272a4', marginTop: '10px' }}>
                                                        Total: <strong style={{ color: '#50fa7b' }}>{(this.props.subscriptionStats?.quarterly?.revenue || 0).toLocaleString()} F</strong>
                                                    </div>
                                                </div>
                                            </Col>
                                            <Col md={4}>
                                                <div style={{
                                                    backgroundColor: '#282a36',
                                                    borderRadius: '8px',
                                                    padding: '15px',
                                                    marginBottom: '20px',
                                                    borderLeft: '4px solid #8be9fd',
                                                    textAlign: 'center',
                                                    boxShadow: '0 4px 10px rgba(0,0,0,0.2)'
                                                }}>
                                                    <h4 style={{ color: '#8be9fd', marginBottom: '10px' }}>
                                                        <i className="fa fa-calendar"></i> Forfait Mensuel
                                                    </h4>
                                                    <h3 style={{ color: '#f8f8f2' }}>{this.props.subscriptionStats?.premium?.count || 0} utilisateurs</h3>
                                                    <div style={{ color: '#6272a4', marginTop: '10px' }}>
                                                        Total: <strong style={{ color: '#50fa7b' }}>{(this.props.subscriptionStats?.premium?.revenue || 0).toLocaleString()} F</strong>
                                                    </div>
                                                </div>
                                            </Col>
                                        </Row>

                                        <div style={{
                                            backgroundColor: '#f8f9fa',
                                            borderRadius: '8px',
                                            padding: '15px',
                                            marginTop: '10px',
                                            textAlign: 'center'
                                        }}>
                                            <h4>Répartition des Forfaits</h4>
                                            <div style={{ display: 'flex', justifyContent: 'center', gap: '20px', marginTop: '15px' }}>
                                                <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                                                    <div style={{ width: '15px', height: '15px', backgroundColor: '#f39c12', borderRadius: '3px' }}></div>
                                                    <span>Annuel ({this.props.subscriptionStats?.annual?.count || 0})</span>
                                                </div>
                                                <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                                                    <div style={{ width: '15px', height: '15px', backgroundColor: '#9b59b6', borderRadius: '3px' }}></div>
                                                    <span>Trimestriel ({this.props.subscriptionStats?.quarterly?.count || 0})</span>
                                                </div>
                                                <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                                                    <div style={{ width: '15px', height: '15px', backgroundColor: '#2ecc71', borderRadius: '3px' }}></div>
                                                    <span>Mensuel ({this.props.subscriptionStats?.premium?.count || 0})</span>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                }
                            />
                        </Col>
                    </Row>
                </Grid>
            </div>
        );
    }
}

AdminDashboard.propTypes = {
    getUserStats: PropTypes.func.isRequired,
    getSubscriptionStats: PropTypes.func.isRequired,
    getConnectedUsersCount: PropTypes.func.isRequired,
    getTotalRevenue: PropTypes.func.isRequired,
    getProductStats: PropTypes.func.isRequired,
    getSubstanceStats: PropTypes.func.isRequired,
    getEquivalenceStats: PropTypes.func.isRequired,
    getAdditionalStats: PropTypes.func.isRequired,
    getActiveUsersStats: PropTypes.func.isRequired,
    totalUsers: PropTypes.number,
    totalSubscribers: PropTypes.number,
    connectedUsers: PropTypes.number,
    totalRevenue: PropTypes.number,
    monthlyStats: PropTypes.object,
    totalProducts: PropTypes.number,
    totalSubstances: PropTypes.number,
    totalEquivalences: PropTypes.number,
    totalInteractions: PropTypes.number,
    totalPosologies: PropTypes.number,
    totalLaboratories: PropTypes.number,
    subscriptionStats: PropTypes.shape({
        annual: PropTypes.shape({
            count: PropTypes.number,
            revenue: PropTypes.number
        }),
        quarterly: PropTypes.shape({
            count: PropTypes.number,
            revenue: PropTypes.number
        }),
        premium: PropTypes.shape({
            count: PropTypes.number,
            revenue: PropTypes.number
        })
    })
};

const mapStateToProps = (state) => ({
    totalUsers: state.stats.totalUsers,
    totalSubscribers: state.stats.totalSubscribers,
    connectedUsers: state.stats.connectedUsers,
    totalActiveUsers: state.stats.totalActiveUsers,
    activeThresholdDays: state.stats.activeThresholdDays,
    totalRevenue: state.stats.totalRevenue,
    monthlyStats: state.stats.monthlyStats,
    totalProducts: state.stats.totalProducts,
    totalSubstances: state.stats.totalSubstances,
    totalEquivalences: state.stats.totalEquivalences,
    totalInteractions: state.stats.totalInteractions,
    totalPosologies: state.stats.totalPosologies,
    totalLaboratories: state.stats.totalLaboratories,
    subscriptionStats: state.stats.subscriptionStats || {
        annual: { count: 0, revenue: 0 },
        quarterly: { count: 0, revenue: 0 },
        premium: { count: 0, revenue: 0 }
    }
});

const mapDispatchToProps = {
    getUserStats,
    getSubscriptionStats,
    getConnectedUsersCount,
    getTotalRevenue,
    getProductStats,
    getSubstanceStats,
    getEquivalenceStats,
    getAdditionalStats,
    getActiveUsersStats,
    updateCurrentUserLastActive
};

export default connect(mapStateToProps, mapDispatchToProps)(AdminDashboard);